// frontend_react/src/components/MenuItemsPanel.jsx

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  getRestaurantCategories,
  getRestaurantItems,
  createRestaurantItem,
  updateRestaurantItem,
  deleteRestaurantItem,
} from "../../services/adminApi";
import logger from "../../utils/logger";
import { useAdminScope } from "../../context/AdminScopeContext";
import {
  AdminErrorState,
  AdminLoadingState,
} from "../../pages/admin/ui_engine";
import { AdminButton } from "../../pages/admin/action_engine";
import { useAdminConfirmation } from "../../pages/admin/panel_engine/consumer";

import {
  AdminActions,
} from "../../pages/admin/section_engine";

const EMPTY_FORM = {
  uuid: null,
  category_uuid: "",
  name: "",
  description: "",
  price_ht: 0,
  vat_rate: 5.5,
  is_soft: false,
  is_hard: false,
  alcohol_degree: "",
  stock: 0,
};

export default function MenuItemsPanel() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const { selectedCompanyUuid } = useAdminScope();
  const confirm = useAdminConfirmation();

  const companyScope = selectedCompanyUuid
    ? { companyUuid: selectedCompanyUuid }
    : {};

  const loadData = async () => {
    setLoading(true);
    setError("");

    try {
      const [catRes, itemRes] = await Promise.all([
        getRestaurantCategories(companyScope),
        getRestaurantItems(companyScope),
      ]);

      setCategories(catRes.data || []);
      setItems(itemRes.data || []);
    } catch (err) {
      logger.error(err);
      setError("Erreur lors du chargement des éléments du menu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedCompanyUuid]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    const payload = {
      ...form,
      price_ht: Number(form.price_ht || 0),
      vat_rate: Number(form.vat_rate || 0),
      stock: Number(form.stock || 0),
      alcohol_degree: form.is_hard && form.alcohol_degree !== ""
        ? Number(form.alcohol_degree)
        : null,
    };

    try {
      if (form.uuid) {
        await updateRestaurantItem(
          form.uuid,
          payload,
          companyScope
        );
        setMessage("Article mis à jour.");
      } else {
        await createRestaurantItem(
          payload,
          companyScope
        );
        setMessage("Article ajouté.");
      }

      resetForm();
      await loadData();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de l’enregistrement de l’article."
      );
    }
  };

  const handleEdit = (item) => {
    setMessage("");
    setError("");

    setForm({
      uuid: item.uuid,
      category_uuid: item.category_uuid || "",
      name: item.name || "",
      description: item.description || "",
      price_ht: item.price_ht ?? 0,
      vat_rate: item.vat_rate ?? 5.5,
      is_soft: Boolean(item.is_soft),
      is_hard: Boolean(item.is_hard),
      alcohol_degree: item.alcohol_degree || "",
      stock: item.stock || 0,
    });
  };

  const handleDelete = async (uuid) => {
    const confirmed = await confirm({
      title: "Supprimer l’article ?",
      message: "Supprimer cet article du menu ?",
      confirmLabel: "Supprimer",
      cancelLabel: "Conserver",
      intent: "danger",
    });

    if (!confirmed) return;

    setMessage("");
    setError("");

    try {
      await deleteRestaurantItem(
        uuid,
        companyScope
      );
      setMessage("Article supprimé.");
      await loadData();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de la suppression de l’article."
      );
    }
  };

  const getCategoryName = (uuid) => {
    return categories.find((cat) => cat.uuid === uuid)?.name || "Sans catégorie";
  };

  if (loading) {
    return (
      <AdminLoadingState message="Chargement du menu..." />
    );
  }

  return (
    <Wrapper>
      {message && <PanelMessage>{message}</PanelMessage>}
      {error && (
        <AdminErrorState
          message={error}
          compact
        />
      )}

      <FormCard onSubmit={handleSubmit}>
        <CardTitle>
          {form.uuid ? "Modifier un article" : "Nouvel article"}
        </CardTitle>

        <Field>
          <label htmlFor="category_uuid">
            Catégorie *
          </label>

          <select
            id="category_uuid"
            name="category_uuid"
            value={form.category_uuid}
            onChange={handleChange}
            required
          >
            <option value="">— Catégorie —</option>
            {categories.map((cat) => (
              <option key={cat.uuid} value={cat.uuid}>
                {cat.name}
              </option>
            ))}
          </select>
        </Field>

        <Field>
          <label htmlFor="item_name">
            Nom article *
          </label>

          <input
            id="item_name"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
          />
        </Field>

        <Field>
          <label>Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
          />
        </Field>

        <FieldGrid>
          <Field>
            <label htmlFor="price_ht">
              Prix HT *
            </label>

            <input
              id="price_ht"
              name="price_ht"
              type="number"
              step="0.01"
              value={form.price_ht}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <label htmlFor="alcohol_degree">
              Degré alcool (%)
            </label>

            <input
              id="alcohol_degree"
              name="alcohol_degree"
              type="number"
              step="0.01"
              value={form.vat_rate}
              onChange={handleChange}
            />
          </Field>
        </FieldGrid>

        <CheckGrid>
          <CheckLabel>
            <input
              name="is_soft"
              type="checkbox"
              checked={form.is_soft}
              onChange={handleChange}
            />
            Boisson non alcoolisée
          </CheckLabel>

          <CheckLabel>
            <input
              name="is_hard"
              type="checkbox"
              checked={form.is_hard}
              onChange={handleChange}
            />
            Boisson alcoolisée
          </CheckLabel>
        </CheckGrid>

        {form.is_hard && (
          <Field>
            <label>Degré alcool (%)</label>
            <input
              name="alcohol_degree"
              type="number"
              step="0.1"
              value={form.alcohol_degree}
              onChange={handleChange}
            />
          </Field>
        )}

        <Field>
          <label>Stock disponible</label>
          <input
            name="stock"
            type="number"
            step="0.01"
            value={form.stock}
            onChange={handleChange}
          />
        </Field>

        <AdminActions $compact>
          <AdminButton
            type="submit"
            variant="success"
            fullWidth
          >
            {form.uuid ? "Enregistrer" : "Ajouter"}
          </AdminButton>

          {form.uuid && (
            <AdminButton
              type="button"
              variant="ghost"
              fullWidth
              onClick={resetForm}
            >
              Annuler
            </AdminButton>
          )}
        </AdminActions>
      </FormCard>

      <ItemList>
        {items.map((item) => (
          <ItemCard key={item.uuid}>
            <TopLine>
              <div>
                <strong>{item.name}</strong>
                <small>{getCategoryName(item.category_uuid)}</small>
              </div>

              <PriceBadge>{Number(item.price_ht || 0).toFixed(2)} € HT</PriceBadge>
            </TopLine>

            {item.description && <Description>{item.description}</Description>}

            <MetaGrid>
              <span>TVA : {item.vat_rate}%</span>
              <span>Stock : {item.stock}</span>
              {item.is_soft && <span>Soft</span>}
              {item.is_hard && <span>Alcool : {item.alcohol_degree || "-"}%</span>}
            </MetaGrid>

            <AdminActions $compact>
              <AdminButton
                type="button"
                variant="ghost"
                fullWidth
                onClick={() => handleEdit(item)}
              >
                Modifier
              </AdminButton>

              <AdminButton
                type="button"
                variant="danger"
                fullWidth
                onClick={() => handleDelete(item.uuid)}
              >
                Supprimer
              </AdminButton>
            </AdminActions>
          </ItemCard>
        ))}
      </ItemList>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  color: #f7f7f7;
`;

const FormCard = styled.form`
  display: grid;
  gap: 0.8rem;
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const CardTitle = styled.h4`
  margin: 0 0 0.3rem;
  font-size: 1rem;
  font-weight: 950;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const CheckGrid = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.82;
  }

  input,
  select,
  textarea {
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  textarea {
    min-height: 80px;
    resize: vertical;
  }
`;

const CheckLabel = styled.label`
  display: flex;
  gap: 0.55rem;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 800;
  opacity: 0.85;

  input {
    width: 18px;
    height: 18px;
  }
`;

const ItemList = styled.div`
  display: grid;
  gap: 0.9rem;
  margin-top: 1rem;
`;

const ItemCard = styled.article`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const TopLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;

  strong {
    display: block;
    font-weight: 950;
  }

  small {
    display: block;
    margin-top: 0.25rem;
    opacity: 0.7;
  }
`;

const PriceBadge = styled.span`
  height: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.75rem;
  font-weight: 950;
  white-space: nowrap;
`;

const Description = styled.p`
  margin: 0.75rem 0 0;
  opacity: 0.75;
  line-height: 1.45;
`;

const MetaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.8rem;

  span {
    padding: 0.28rem 0.55rem;
    border-radius: 999px;
    background: rgba(255, 249, 234, 0.1);
    color: #fff9ea;
    font-size: 0.75rem;
    font-weight: 800;
  }
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: rgba(40, 140, 80, 0.9);
  font-weight: 850;
`;
